// src/pages/admins/ProjectDependencyModal.jsx
//
// "Project Cannot Be Deleted" resolution modal -- replaces the old
// toast-only PROJECT_IN_USE handling in ProjectManagementPage.jsx. Lets an
// admin inspect and resolve the exact records blocking a project delete
// (members/modules/tasks) without leaving /admin/projects, then retries the
// delete once every blocker is cleared.
//
// Reuses existing APIs only: GET /projects/:id/dependencies (new, read-only
// aggregate), DELETE /projects/:id/members/:membershipId (existing soft
// "remove from project"), DELETE and PATCH .../active-status on
// /projects/:id/modules/:moduleId (existing module delete/deactivate), and
// DELETE /projects/:id (existing project delete). Never deletes a user
// account, never cascades a module/task delete -- every mutation here is
// something the existing Members/Modules tabs already do one at a time.
import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner, Table, Badge } from "react-bootstrap";
import api from "../../api";
import { usePermissions } from "../../context/PermissionContext";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/common/ConfirmModal";

export default function ProjectDependencyModal({
  show,
  project,
  initialCounts,
  onHide,
  onDeleted,
  onDependencyChanged,
  onManageModules,
}) {
  const { can } = usePermissions();

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [deps, setDeps] = useState(null);

  const [deleting, setDeleting] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [deleteModuleTarget, setDeleteModuleTarget] = useState(null);
  const [deletingModule, setDeletingModule] = useState(false);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quickActionBusy, setQuickActionBusy] = useState(false);

  useEffect(() => {
    if (show && project) {
      setExpanded(false);
      setDeps(initialCounts ? { counts: initialCounts, members: [], modules: [], tasks: [], tatRules: [] } : null);
      fetchDependencies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, project?.id]);

  const fetchDependencies = async () => {
    if (!project) return;
    setLoading(true);
    setLoadError("");
    try {
      const res = await api.get(`/projects/${project.id}/dependencies`);
      setDeps(res.data?.data || null);
    } catch (err) {
      console.error("Fetch project dependencies error:", err);
      setLoadError(err.response?.data?.message || "Failed to load dependency details");
    } finally {
      setLoading(false);
    }
  };

  const afterMutation = async () => {
    await fetchDependencies();
    onDependencyChanged && onDependencyChanged();
  };

  const confirmRemoveMember = async () => {
    if (!removeMemberTarget || !project) return;
    setRemovingMember(true);
    try {
      await api.delete(`/projects/${project.id}/members/${removeMemberTarget.projectMemberId}`);
      toast.success("Project member removed");
      setRemoveMemberTarget(null);
      await afterMutation();
    } catch (err) {
      console.error("Remove member error:", err);
      toast.error(err.response?.data?.message || "Failed to remove project member");
    } finally {
      setRemovingMember(false);
    }
  };

  const confirmDeleteModule = async () => {
    if (!deleteModuleTarget || !project) return;
    setDeletingModule(true);
    try {
      await api.delete(`/projects/${project.id}/modules/${deleteModuleTarget.id}`);
      toast.success("Module deleted");
      setDeleteModuleTarget(null);
      await afterMutation();
    } catch (err) {
      console.error("Delete module error:", err);
      const data = err.response?.data;
      if (data?.code === "MODULE_IN_USE") {
        toast.error("This module still has its own members or tasks -- manage it before deleting.");
      } else {
        toast.error(data?.message || "Failed to delete module");
      }
    } finally {
      setDeletingModule(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!project) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success("Project deleted");
      onDeleted && onDeleted();
    } catch (err) {
      console.error("Delete project error:", err);
      const data = err.response?.data;
      if (data?.code === "PROJECT_IN_USE") {
        toast.error("New dependencies appeared -- refreshing details.");
        await fetchDependencies();
      } else {
        toast.error(data?.message || "Failed to delete project");
      }
    } finally {
      setDeleting(false);
    }
  };

  const confirmQuickAction = async () => {
    if (!deps || !project) return;
    setQuickActionBusy(true);
    try {
      for (const m of deps.members) {
        await api.delete(`/projects/${project.id}/members/${m.projectMemberId}`);
      }
      await api.delete(`/projects/${project.id}`);
      toast.success("Members removed and project deleted");
      setShowQuickAction(false);
      onDependencyChanged && onDependencyChanged();
      onDeleted && onDeleted();
    } catch (err) {
      console.error("Quick action error:", err);
      toast.error(err.response?.data?.message || "Failed to complete this action");
      setShowQuickAction(false);
      await afterMutation();
    } finally {
      setQuickActionBusy(false);
    }
  };

  if (!project) return null;

  const counts = deps?.counts || initialCounts || { members: 0, modules: 0, tasks: 0, tatRules: 0 };
  const resolved =
    expanded && deps && deps.counts.members + deps.counts.modules + deps.counts.tasks + (deps.counts.tatRules || 0) === 0;

  const canRemoveMember = can("project_members", "remove") || can("project_members", "manage");
  const canDeleteModule = can("project_modules", "delete");
  const canManageModules = can("project_modules", "view");
  const canViewTask = can("tasks", "view");
  const canViewTatRules = can("tat_rules", "view");

  // Only members that are still ACTIVE can actually be resolved from here --
  // "Remove from Project" soft-deletes (is_active=0) but the row (and its
  // project_id) still exists, so an already-inactive/historical member row
  // can never be cleared through this modal (see services/projectService.js's
  // getDependentCounts() comment: fk_project_members_project is ON DELETE
  // RESTRICT and does not care about is_active). The quick action must not
  // be offered when it can't actually reach zero.
  const allBlockingMembersActive = !!deps && deps.members.length > 0 && deps.members.every((m) => m.isActive);
  const quickActionEligible =
    expanded &&
    deps &&
    deps.counts.modules === 0 &&
    deps.counts.tasks === 0 &&
    (deps.counts.tatRules || 0) === 0 &&
    deps.counts.members > 0 &&
    allBlockingMembersActive &&
    canRemoveMember;

  const hasUnresolvableBlocker =
    expanded && deps && !resolved && (deps.members.some((m) => !m.isActive) || (deps.counts.tatRules || 0) > 0);

  return (
    <>
      <Modal
        show={show}
        onHide={deleting ? undefined : onHide}
        centered
        size={expanded ? "lg" : undefined}
        className="unit-modal"
        dialogClassName={expanded ? "project-admin-modal project-admin-modal-lg project-admin-modal-danger" : "project-admin-modal project-admin-modal-sm project-admin-modal-danger"}
        backdropClassName="project-admin-backdrop"
      >
        <Modal.Header closeButton={!deleting} closeLabel="Close">
          <div className="project-modal-heading">
            <div className="project-modal-icon project-modal-icon-danger">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
            </div>
            <div>
              <small>Delete Action</small>
              <Modal.Title>Project Cannot Be Deleted</Modal.Title>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {loading && !deps ? (
            <div className="unit-loader">
              <Spinner animation="border" />
              <p>Loading dependency details...</p>
            </div>
          ) : loadError && !deps ? (
            <div className="unit-empty">
              <p className="text-danger">{loadError}</p>
              <Button className="unit-btn unit-btn-light" onClick={fetchDependencies}>Retry</Button>
            </div>
          ) : resolved ? (
            <>
              <p className="unit-delete-text text-center">
                All blocking dependencies have been resolved.
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>{project.name}</strong> still has related records.
              </p>

              <Table borderless size="sm" className="mb-3">
                <tbody>
                  <tr>
                    <td>Members</td>
                    <td className="text-end"><Badge bg={counts.members > 0 ? "danger" : "secondary"}>{counts.members}</Badge></td>
                  </tr>
                  <tr>
                    <td>Modules</td>
                    <td className="text-end"><Badge bg={counts.modules > 0 ? "danger" : "secondary"}>{counts.modules}</Badge></td>
                  </tr>
                  <tr>
                    <td>Tasks</td>
                    <td className="text-end"><Badge bg={counts.tasks > 0 ? "danger" : "secondary"}>{counts.tasks}</Badge></td>
                  </tr>
                  <tr>
                    <td>TAT Rules</td>
                    <td className="text-end"><Badge bg={(counts.tatRules || 0) > 0 ? "danger" : "secondary"}>{counts.tatRules || 0}</Badge></td>
                  </tr>
                </tbody>
              </Table>

              {!expanded && (
                <p className="text-muted mb-0">
                  Resolve the related records before deleting this project.
                </p>
              )}

              {expanded && loading && (
                <div className="unit-loader">
                  <Spinner animation="border" size="sm" />
                  <p>Refreshing...</p>
                </div>
              )}

              {expanded && !loading && loadError && (
                <div className="unit-empty mb-3">
                  <p className="text-danger mb-2">{loadError}</p>
                  <Button size="sm" className="unit-btn unit-btn-light" onClick={fetchDependencies}>Retry</Button>
                </div>
              )}

              {expanded && deps && deps.members.length > 0 && (
                <div className="mb-4">
                  <h6>Related Members ({deps.members.length})</h6>
                  <Table responsive size="sm" className="unit-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Employee Code</th>
                        <th>Department</th>
                        <th>Role in Project</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deps.members.map((m) => (
                        <tr key={m.projectMemberId}>
                          <td>{m.name}</td>
                          <td>{m.employeeCode || "—"}</td>
                          <td>{m.department || "—"}</td>
                          <td>{m.membershipType}</td>
                          <td><Badge bg={m.isActive ? "success" : "secondary"}>{m.isActive ? "Active" : "Historical"}</Badge></td>
                          <td className="text-end">
                            {!m.isActive ? (
                              <span className="text-muted small">Historical membership — preserved, cannot be removed further</span>
                            ) : canRemoveMember ? (
                              <Button
                                size="sm"
                                className="unit-btn unit-btn-danger"
                                onClick={() => setRemoveMemberTarget(m)}
                              >
                                Remove from Project
                              </Button>
                            ) : (
                              <span className="text-muted small">No permission to remove</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {expanded && deps && deps.modules.length > 0 && (
                <div className="mb-4">
                  <h6>Related Modules ({deps.modules.length})</h6>
                  <Table responsive size="sm" className="unit-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Members</th>
                        <th>Tasks</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deps.modules.map((m) => (
                        <tr key={m.id}>
                          <td>{m.name}</td>
                          <td>{m.memberCount}</td>
                          <td>{m.taskCount}</td>
                          <td><Badge bg={m.isActive ? "success" : "secondary"}>{m.isActive ? "Active" : "Archived"}</Badge></td>
                          <td className="text-end">
                            <div className="unit-actions justify-content-end">
                              {canManageModules ? (
                                <Button
                                  size="sm"
                                  className="unit-btn unit-btn-light"
                                  onClick={() => onManageModules && onManageModules(project.id)}
                                >
                                  Manage Module
                                </Button>
                              ) : (
                                <span className="text-muted small">No permission to manage</span>
                              )}
                              {m.canDelete && canDeleteModule && (
                                <Button
                                  size="sm"
                                  className="unit-btn unit-btn-danger"
                                  onClick={() => setDeleteModuleTarget(m)}
                                >
                                  Delete Module
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {expanded && deps && deps.tasks.length > 0 && (
                <div className="mb-2">
                  <h6>Related Tasks ({deps.tasks.length})</h6>
                  <Table responsive size="sm" className="unit-table">
                    <thead>
                      <tr>
                        <th>Task</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deps.tasks.map((t) => (
                        <tr key={t.id}>
                          <td>TASK-{t.id} — {t.title}</td>
                          <td>{t.status || "—"}</td>
                          <td className="text-end">
                            {canViewTask ? (
                              <a
                                className="unit-btn unit-btn-light btn btn-sm"
                                href={`/admin/tasks/${t.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Task
                              </a>
                            ) : (
                              <span className="text-muted small">No permission to view</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {expanded && deps && deps.tatRules && deps.tatRules.length > 0 && (
                <div className="mb-2">
                  <h6>Related TAT Rules ({deps.tatRules.length})</h6>
                  <Table responsive size="sm" className="unit-table">
                    <thead>
                      <tr>
                        <th>Rule</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deps.tatRules.map((r) => (
                        <tr key={r.id}>
                          <td>{r.name} {r.code ? <span className="text-muted">({r.code})</span> : null}</td>
                          <td><Badge bg={r.isActive ? "success" : "secondary"}>{r.isActive ? "Active" : "Inactive"}</Badge></td>
                          <td className="text-end">
                            {canViewTatRules ? (
                              <a
                                className="unit-btn unit-btn-light btn btn-sm"
                                href="/admin/tat-rules"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Manage TAT Rules
                              </a>
                            ) : (
                              <span className="text-muted small">No permission to manage</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {hasUnresolvableBlocker && (
                <p className="text-muted small mb-0">
                  Some of these records (historical memberships, TAT rules) can't be removed from here and this project can never be
                  hard-deleted while they exist. Use Archive Project instead if you don't need to permanently delete it.
                </p>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={onHide} disabled={deleting}>
            Cancel
          </Button>

          {!expanded && !loadError && (
            <Button className="unit-btn unit-btn-primary" onClick={() => setExpanded(true)}>
              Resolve Dependencies
            </Button>
          )}

          {resolved && (
            <Button className="unit-btn unit-btn-danger" onClick={handleDeleteProject} disabled={deleting}>
              {deleting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                "Delete Project"
              )}
            </Button>
          )}

          {quickActionEligible && !resolved && (
            <Button className="unit-btn unit-btn-danger" onClick={() => setShowQuickAction(true)}>
              Remove {deps.counts.members} Member{deps.counts.members === 1 ? "" : "s"} &amp; Delete Project
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={!!removeMemberTarget}
        onHide={() => setRemoveMemberTarget(null)}
        onConfirm={confirmRemoveMember}
        busy={removingMember}
        title="Remove Project Member?"
        message={`Remove ${removeMemberTarget?.name} from ${project.name}? This only removes the project membership. The employee account will not be deleted.`}
        confirmLabel="Remove"
        dialogClassName="project-admin-modal project-admin-modal-sm project-admin-modal-danger"
      />

      <ConfirmModal
        show={!!deleteModuleTarget}
        onHide={() => setDeleteModuleTarget(null)}
        onConfirm={confirmDeleteModule}
        busy={deletingModule}
        title="Delete Module?"
        message={`Delete module "${deleteModuleTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        dialogClassName="project-admin-modal project-admin-modal-sm project-admin-modal-danger"
      />

      <ConfirmModal
        show={showQuickAction}
        onHide={() => setShowQuickAction(false)}
        onConfirm={confirmQuickAction}
        busy={quickActionBusy}
        title="Remove Members & Delete Project?"
        message={
          deps
            ? `This will remove ${deps.counts.members} project membership(s) from "${project.name}". The employee account(s) will NOT be deleted. The project will then be deleted. This cannot be undone.`
            : ""
        }
        confirmLabel="Remove & Delete"
        dialogClassName="project-admin-modal project-admin-modal-sm project-admin-modal-danger"
      />
    </>
  );
}
